"use client";

import { Box, Flex } from "@chakra-ui/react";
import type { MouseEvent } from "react";
import type { GetPolicyResponse } from "@/openapi/generated";
import { Link, Text } from "@/components/ui";
import { useThemeTokens } from "@/lib/theme/tokens";

export function waiverAnchorId(policyId: GetPolicyResponse["policyId"]): string {
  return `waiver-${String(policyId)}`;
}

export function scrollToTermsWaiver(anchorId: string) {
  const element = document.getElementById(anchorId);

  if (!element) return;

  element.scrollIntoView({ behavior: "smooth", block: "start" });
  window.history.replaceState(null, "", `#${anchorId}`);
}

interface TermsTableOfContentsProps {
  waivers: GetPolicyResponse[];
  variant?: "public" | "portal";
}

export const TermsTableOfContents = ({
  waivers,
  variant = "portal",
}: TermsTableOfContentsProps) => {
  const theme = useThemeTokens();

  if (waivers.length < 2 || variant === "public") return null;

  const handleJumpClick = (
    event: MouseEvent<HTMLAnchorElement>,
    anchorId: string,
  ) => {
    event.preventDefault();
    scrollToTermsWaiver(anchorId);
  };

  return (
    <Box
      as="nav"
      aria-label="Table of contents"
      py={4}
      borderTopWidth="1px"
      borderBottomWidth="1px"
      borderColor={theme.borderGlass.default}
    >
      <Text fontSize="sm" fontWeight="500" color={theme.text.tertiary} mb={3}>
        Jump to
      </Text>
      <Flex as="ul" direction="column" gap={2} m={0} p={0} listStyleType="none">
        {waivers.map((waiver) => {
          const anchorId = waiverAnchorId(waiver.policyId);

          return (
            <Box as="li" key={String(waiver.policyId)}>
              <Link
                href={`#${anchorId}`}
                onClick={(event) => handleJumpClick(event, anchorId)}
                fontSize="sm"
                fontWeight="500"
                color={theme.text.secondary}
                lineHeight="1.5"
                sx={{
                  "@media (hover: hover)": {
                    "&:hover": {
                      color: theme.accent.primary,
                      textDecoration: "underline",
                    },
                  },
                }}
              >
                {waiver.name}
              </Link>
            </Box>
          );
        })}
      </Flex>
    </Box>
  );
};
